import { Roles } from '@dataquiver/stoneage-roles';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GenericMessages } from '../../intl/GenericMessages';
import { ProfileMessages } from '../../intl/ProfileMessages';
import { Organization } from '../../models/organization';
import { User, UserStatus, ValidUserStatuses } from '../../models/user';
import { getOrgName } from '../../utils/helpers/referenceHelpers';
import { DqLoading } from '../DqLoading';
import { SearchResponse } from '../../utils/types';
import { debounce } from 'lodash';
import { SearchUsersRequest } from '../../redux/api/users';
import moment from 'moment';
import SentinelLogo from '../../assets/images/SentinelLogo.png';

export interface StoneAgeUsersTableProps {
  loading: boolean;
  data?: SearchResponse<User>;
  request: SearchUsersRequest;
  organizations: Organization[];
  onSelection: (user: User) => void;
  onTableChange: (request: Partial<SearchUsersRequest>) => void;
}

export const StoneAgeUsersTable: React.FC<StoneAgeUsersTableProps> = ({
  loading,
  onSelection,
  data,
  request,
  organizations,
  onTableChange,
}) => {
  const { formatMessage } = useIntl();
  /**
   * Debounce a function for updating the search term query, we don't want
   * to fire off a request every key-press
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSearchTerm = useCallback(
    debounce(
      (searchTerm: string | undefined) => {
        onTableChange({
          searchTerm: searchTerm,
        });
      },
      250,
      { maxWait: 1000 },
    ),
    [],
  );

  const columns: MUIDataTableColumnDef[] = [
    {
      label: formatMessage(GenericMessages.idColumnLabel),
      name: '_id', // must be column 0! That way it is data[0] on rowClick. Thanks.
      options: {
        display: false,
        filter: false,
      },
    },
    {
      label: formatMessage(GenericMessages.emailColumnLabel),
      name: 'email',
      options: {
        filter: false,
      },
    },
    {
      label: formatMessage(GenericMessages.lastNameColumnLabel),
      name: 'family_name',
      options: {
        filter: false,
      },
    },
    {
      label: formatMessage(GenericMessages.firstNameColumnLabel),
      name: 'given_name',
      options: {
        filter: false,
      },
    },
    {
      label: formatMessage(GenericMessages.organizationColumnLabel),
      name: 'org',
      options: {
        customBodyRender: (value: string) => getOrgName(value, organizations),
        filterOptions: {
          names: organizations.map(org => org.orgName),
        },
        filterList: request?.organizationIds ?? [],
        customFilterListOptions: {
          render: value => getOrgName(value, organizations),
        },
        sort: false,
      },
    },
    {
      label: formatMessage(GenericMessages.roleColumnLabel),
      name: 'role',
      options: {
        customBodyRenderLite: (dataIndex: number) => Roles.getPrettyPrint(data?.results[dataIndex]?.role),
        filterOptions: {
          renderValue: option => Roles.getPrettyPrint(Number(option)),
          names: Roles.ROLES_ARRAY.map(r => r.value.toString()),
        },
        filterList: request?.roles?.map(s => s!.toString()) ?? [],
        customFilterListOptions: {
          render: value => Roles.getPrettyPrint(Number(value)),
        },
      },
    },
    {
      label: formatMessage(GenericMessages.statusLabel),
      name: 'status',
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const val = data?.results[dataIndex]?.status;
          if (!val) {
            return formatMessage(GenericMessages.unknownPlaceholder);
          }
          return val.charAt(0).toUpperCase() + val.slice(1);
        },
        filterOptions: {
          renderValue: option => {
            if (!option) {
              return '';
            }
            return option.charAt(0).toUpperCase() + option.slice(1);
          },
          names: ValidUserStatuses.filter(i => !!i).map(i => i!.toString()),
        },
        filterList: request?.statuses?.filter(s => !!s).map(s => s!.toString()) ?? [],
        customFilterListOptions: {
          render: value => value.charAt(0).toUpperCase() + value.slice(1),
        },
      },
    },
    {
      label: formatMessage(GenericMessages.tosLabel),
      name: 'sentinelLinkTOSAcceptedAt',
      options: {
        sort: false,
        display: false,
        customBodyRender: (value?: string) => {
          if (!value) return '';
          return moment(value).format();
        },
      },
    },
  ];

  const handleTableChange = (eventType: string, tableState: MUIDataTableState) => {
    switch (eventType) {
      case 'viewColumnsChange':
        break; // Columns not configurable for this view.
      case 'sort':
        onTableChange({
          sort: { property: tableState.sortOrder.name as keyof User, order: tableState.sortOrder.direction },
          pageNumber: 0,
          pageSize: tableState.rowsPerPage,
        });
        break;
      case 'changePage':
        onTableChange({
          pageNumber: tableState.page,
          pageSize: tableState.rowsPerPage,
        });
        break;
      case 'changeRowsPerPage':
        onTableChange({
          pageNumber: tableState.page,
          pageSize: tableState.rowsPerPage,
        });
        break;
      case 'filterChange':
        const requestFilters: Partial<SearchUsersRequest> = {
          roles: undefined,
          organizationIds: undefined,
          statuses: undefined,
        };

        for (let i = 0; i < tableState.filterList.length; i++) {
          if (tableState.filterList[i].length > 0) {
            const columnName = tableState.columns[i].name;
            const values = tableState.filterList[i];

            if (columnName === 'org' && organizations) {
              requestFilters.organizationIds = values
                .map(name => organizations.find(org => org.orgName === name)?._id)
                .filter(i => !!i) as string[];
            } else if (columnName === 'role') {
              requestFilters.roles = values.map(v => Number(v));
            } else if (columnName === 'status') {
              requestFilters.statuses = values as UserStatus[];
            }
          }
        }

        onTableChange(requestFilters);
        break;
      case 'search':
        setSearchTerm(tableState.searchText ?? undefined);
        break;
    }
  };

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    onRowClick: (_, rowData) => {
      const user = data?.results[rowData.dataIndex];
      // No edit functionality for non-StoneAge Users
      if (!user || !Roles.isStoneAge(user.role)) return;

      onSelection(user);
    },
    onTableChange: (eventType: string, tableState: MUIDataTableState) => handleTableChange(eventType, tableState),
    rowsPerPageOptions: [10, 25, 50],
    print: false,
    download: true,
    serverSide: true,
    count: data?.totalCount,
    textLabels: {
      body: {
        noMatch: loading ? (
          <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <DqLoading width="100px" img={SentinelLogo} />
          </div>
        ) : (
          <FormattedMessage {...ProfileMessages.stoneAgeUsersGridEmptyLabel} />
        ),
      },
    },
  } as MUIDataTableOptions;

  return <MUIDataTable columns={columns} data={data?.results ?? []} options={options} title={undefined} />;
};
